import { PrestationParticipationStatus } from '@cpce/database/enums';
import {
  type BaseBeneficiary,
  type BaseEmployeeBeneficiaryWithEmployee,
  type BasePrestationParticipation,
  type Beneficiary,
  type BeneficiaryWithEmployees,
  DocumentStatus,
  type EmployeeBeneficiaryDocument,
  EmployeeBeneficiaryDocumentType,
  Gender,
} from '@cpce/shared';
import type {
  BeneficiaryAlerts,
  BeneficiaryFormatted,
  BeneficiaryWithEmployeesFormatted,
  EmployeeBeneficiaryDocumentFormatted,
  EmployeeBeneficiaryWithEmployeeFormatted,
  PrestationParticipationFormatted,
} from '../beneficiaries.types';
import { employeeBeneficiaryStatusToString } from '../employees/utils/employees.utils';

export function formatBeneficiary(beneficiary: BaseBeneficiary): BeneficiaryFormatted {
  return {
    ...beneficiary,
    fullName: `${beneficiary.firstName ?? ''} ${beneficiary.lastName ?? ''}`.trim(),
    genderFormatted: beneficiary.gender ? genderToString(beneficiary.gender) : undefined,
    alerts: {
      documentValidationRequired: 0,
    },
    prestationParticipations: beneficiary.prestationParticipations.map(
      formatPrestationParticipation
    ),
    employeeBeneficiaryDocuments: beneficiary.employeeBeneficiaryDocuments.map(
      formatEmployeeBeneficiaryDocument
    ),
  };
}

export function formatBeneficiaryWithEmployees(
  beneficiary: BeneficiaryWithEmployees
): BeneficiaryWithEmployeesFormatted {
  const formattedBeneficiary = formatBeneficiary(beneficiary);
  const formattedEmployeeBeneficiaries = beneficiary.employeeBeneficiaries.map(
    formatEmployeeBeneficiaryWithEmployee
  );

  const { alerts: _, ...beneficiaryWithoutAlerts } = formattedBeneficiary;

  return {
    ...beneficiaryWithoutAlerts,
    prestationParticipations: beneficiary.prestationParticipations.map(
      formatPrestationParticipation
    ),
    employeeBeneficiaryDocuments: beneficiary.employeeBeneficiaryDocuments.map(
      formatEmployeeBeneficiaryDocument
    ),
    alerts: computeBeneficiaryAlerts(beneficiary, beneficiary.employeeBeneficiaryDocuments),
    employeeBeneficiaries: formattedEmployeeBeneficiaries,
  };
}

function formatEmployeeBeneficiaryWithEmployee(
  employeeBeneficiary: BaseEmployeeBeneficiaryWithEmployee
): EmployeeBeneficiaryWithEmployeeFormatted {
  const formattedBeneficiary = formatBeneficiary(employeeBeneficiary.beneficiary);
  const formattedEmployeeBeneficiary = formatBeneficiary(employeeBeneficiary.employee.beneficiary);

  const { beneficiary: _, ...employeeWithoutBeneficiary } = employeeBeneficiary.employee;

  return {
    ...employeeBeneficiary,
    beneficiary: {
      ...formattedBeneficiary,
      prestationParticipations: employeeBeneficiary.beneficiary.prestationParticipations.map(
        formatPrestationParticipation
      ),
      employeeBeneficiaryDocuments:
        employeeBeneficiary.beneficiary.employeeBeneficiaryDocuments.map(
          formatEmployeeBeneficiaryDocument
        ),
      alerts: computeBeneficiaryAlerts(
        employeeBeneficiary.beneficiary,
        employeeBeneficiary.beneficiary.employeeBeneficiaryDocuments
      ),
    },
    employee: {
      ...employeeWithoutBeneficiary,
      beneficiary: formattedEmployeeBeneficiary,
    },
    statusFormatted: employeeBeneficiaryStatusToString(employeeBeneficiary.status),
  };
}

function computeBeneficiaryAlerts(
  _beneficiary: Beneficiary,
  employeeBeneficiaryDocuments: EmployeeBeneficiaryDocument[]
): BeneficiaryAlerts {
  return {
    documentValidationRequired: employeeBeneficiaryDocuments.filter(
      (document) => document.status === DocumentStatus.awaitingValidation
    ).length,
  };
}

function formatPrestationParticipation(
  prestationParticipation: BasePrestationParticipation
): PrestationParticipationFormatted {
  return {
    ...prestationParticipation,
    statusFormatted: prestationParticipationStatusToString(prestationParticipation.status),
  };
}

function prestationParticipationStatusToString(status: PrestationParticipationStatus): string {
  switch (status) {
    case PrestationParticipationStatus.awaitingValidation:
      return 'En attente de validation';
    case PrestationParticipationStatus.validated:
      return 'Validé';
    default:
      return status;
  }
}

export function formatEmployeeBeneficiaryDocument(
  employeeBeneficiaryDocument: EmployeeBeneficiaryDocument
): EmployeeBeneficiaryDocumentFormatted {
  return {
    ...employeeBeneficiaryDocument,
    statusFormatted: documentStatusToString(employeeBeneficiaryDocument.status),
    typeFormatted: employeeBeneficiaryDocumentTypeToString(employeeBeneficiaryDocument.type),
  };
}

export function documentStatusToString(status: DocumentStatus): string {
  switch (status) {
    case DocumentStatus.awaitingValidation:
      return 'En attente de validation';
    case DocumentStatus.validated:
      return 'Validé';
    case DocumentStatus.refused:
      return 'Refusé';
  }
}

function employeeBeneficiaryDocumentTypeToString(type: EmployeeBeneficiaryDocumentType): string {
  switch (type) {
    case EmployeeBeneficiaryDocumentType.familyRecord:
      return 'Livret de famille';
    case EmployeeBeneficiaryDocumentType.schoolCertificate:
      return 'Certificat de scolarité';
  }
}

function genderToString(gender: Gender): string {
  switch (gender) {
    case Gender.man:
      return 'Homme';
    case Gender.woman:
      return 'Femme';
    case Gender.other:
      return 'Autre';
  }
}
